import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

const MultiSelectSearch = ({
  value = [],
  onChange,
  items = [],
  labelKey = "label",
  valueKey = "key",
  placeholder = "Select options...",
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Update dropdown list when user types
  useEffect(() => {
    let available = items.filter(
      (item) => !value.some((v) => v[valueKey] === item[valueKey])
    );

    if (query.trim() !== "") {
      available = available.filter(
        (item) =>
          item[labelKey].toLowerCase().includes(query.toLowerCase()) ||
          item[valueKey].toLowerCase().includes(query.toLowerCase())
      );
    }

    setFilteredItems(available);
  }, [query, value, items, labelKey, valueKey]);

  // Add
  const handleSelect = (item) => {
    const newValue = [
      ...value,
      { [valueKey]: item[valueKey], [labelKey]: item[labelKey] },
    ];
    onChange?.(newValue);
    setQuery("");
    setIsOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // Remove
  const handleRemove = (item) => {
    const newValue = value.filter((v) => v[valueKey] !== item[valueKey]);
    onChange?.(newValue);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-3">
          {value.map((v) => {
            const item = items.find((i) => i[valueKey] === v[valueKey]);
            return (
              <div
                key={v[valueKey]}
                className="flex items-center gap-2 bg-background.card border border-border rounded-full px-3 py-1 shadow-sm"
              >
                {item?.icon && (
                  <img src={item.icon} alt={v[labelKey]} className="w-5 h-5" />
                )}

                <span className="text-sm font-medium text-text.primary">
                  {v[labelKey]}
                </span>

                <button
                  type="button"
                  onClick={() => handleRemove(v)}
                  className="text-text.muted hover:text-btn.brandRed"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="relative" ref={wrapperRef}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full border border-border rounded-lg py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary text-sm font-poppins text-text.primary placeholder:text-text.muted"
        />

        {isOpen && filteredItems.length > 0 && (
          <div className="absolute z-20 bg-white border border-border mt-1 rounded-lg shadow-lg w-full max-h-72 overflow-y-auto">
            {filteredItems.map((item) => (
              <div
                key={item[valueKey]}
                onMouseDown={() => handleSelect(item)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-primary/10 cursor-pointer"
              >
                {item.icon && (
                  <img
                    src={item.icon}
                    alt={item[labelKey]}
                    className="w-5 h-5"
                  />
                )}
                <span className="text-sm font-medium text-text.primary">
                  {item[labelKey]}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectSearch;
